/** @jsxImportSource @emotion/react */
import { FEATURE_FLAGS } from '@app/app-constants'
import { GeocoderButton } from '@app/components/Atoms/Geocoder'
import { Modal } from '@app/components/Atoms/Modal'
import { BaseMapButton } from '@app/components/Pages/Map/BaseMapButton'
import { MapLegends } from '@app/components/Pages/Map/MapLegends'
import { RiskMapTools } from '@app/components/Pages/Map/MapTools/MapTools'
import { ScenariosByAsset } from '@app/components/Property/types'
import { appLink } from '@app/config/appLink'
import apiClient from '@app/config/axios.config'
import { enrichHazardsWithDamageCost } from '@app/hooks/useDamageCost'
import { useFeatureFlags } from '@app/hooks/useFeatureFlags'
import { usePreferences } from '@app/hooks/usePreferences'
import { regionMasksAtom } from '@app/risk-migration/atoms/riskFilters.atoms'
import { visibleAssetsAtom } from '@app/risk-migration/atoms/riskLayers.atoms'
import { useRiskMapNavigation } from '@app/risk-migration/hooks/useRiskMapNavigation'
import {
  riskMapInstanceAtom,
  riskMapInteractiveAtom,
  riskMapPaddingRefAtom,
  selectedFeatureAtom,
  selectedMarkerAtom,
} from '@app/risk-migration/atoms/riskMap.atoms'
import { useDeselectRiskFeature } from '@app/risk-migration/hooks/useDeselectRiskFeature'
import { useRiskLayersState } from '@app/risk-migration/hooks/useRiskLayersState'
import { useSelectRiskFeature } from '@app/risk-migration/hooks/useSelectRiskFeature'
import { addLayersDialogAtom } from '@app/state/layoutAtom'
import { css } from '@emotion/react'
import { Box, useTheme } from '@mui/material'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import mapboxgl, { Map, MapboxGeoJSONFeature, MapLayerMouseEvent } from 'mapbox-gl'
import { useEffect, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import ReactDOMServer from 'react-dom/server'
import { useLocation } from 'react-router-dom'
import { ThreeDButton } from '../../ThreeDButton/ThreeDButton'
import { GenericMapView } from '../GenericMapView/GenericMapView'
import {
  selectionMeasurementContainer,
  selectionMeasurementHeader,
} from '../GenericMapView/GenericMapView.styles'
import {
  addSelectedMarkerToMap,
  getCenterOfFeature,
} from '../GenericMapView/GenericMapView.utilities'
import { AddLayersDialog } from './AddLayersDialog'
import {
  mapBottomLeftContainer,
  mapBottomRightContainer,
  mapOverlayContainer,
  mapTopRightContainer,
} from './RiskMapView.styles'
import {
  getAssetAndHazardDetailsForSelectedFeature,
  getHazardInfoForSelectedFeature,
  getInformationDetailsForSelectedFeature,
  getLayersByType,
} from './RiskMapView.utilities'
import { RiskMapTooltip } from './Tooltip/RiskMapTooltip'
import { useFetchLegends } from '@app/hooks/useFetchLegends'
import { drawAreasAtom } from '@app/risk-migration/atoms/riskFilters.atoms'
import {
  clientLocationAtom as mapClientLocationAtom,
  showAddLayersModalTabAtom,
} from '@app/risk-migration/atoms/riskMap.atoms'
import { clientLocationAtom as configuredClientLocationAtom } from '@app/state/clientsAtom'
import { useLayerDataFetcher } from '../../RiskSideDrawerContent/data_fetchers/layerDataFetcher'

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN

export type AssetHazardInstance = {
  vulnerability_style: number
  vulnerability: string
  metric: number
  exposure_value: string
}
export type AssetHazardInstances = {
  [hazardAssetTag: string]: { [assetId: string]: AssetHazardInstance }
}
export type VulnerabilityData = {
  [assetType: string]: AssetHazardInstances
}

export const RiskMapView = ({ flyToClientLocation = true }: { flyToClientLocation?: boolean }) => {
  const location = useLocation()
  mapboxgl.accessToken = MAPBOX_TOKEN
  const theme = useTheme()
  const [openAddLayersSelectionDialog, setOpenAddLayersSelectionDialog] =
    useAtom(addLayersDialogAtom)
  const mapPaddingRef = useAtomValue(riskMapPaddingRefAtom)
  const map = useAtomValue(riskMapInstanceAtom)
  const interactive = useAtomValue(riskMapInteractiveAtom)
  const selectFeature = useSelectRiskFeature()
  const deselectFeature = useDeselectRiskFeature()
  const { flyTo } = useRiskMapNavigation()
  const [showAddLayersModalTab, setShowAddLayersModalTab] = useAtom(showAddLayersModalTabAtom)
  const { term_preference } = usePreferences()
  const { useFeatureFlag } = useFeatureFlags()
  const isDamageCostEnabled = useFeatureFlag(FEATURE_FLAGS.DAMAGE_COST_REPORTING)
  // Mirror into a ref so the tooltip closure reads the current value without
  // forcing the whole `controlMouseEvents` setup to re-run on flag changes.
  const isDamageCostEnabledRef = useRef(isDamageCostEnabled)
  isDamageCostEnabledRef.current = isDamageCostEnabled
  const layerData = useLayerDataFetcher()
  const selectedFeature = useAtomValue(selectedFeatureAtom)
  const drawAreas = useAtomValue(drawAreasAtom)
  const [selectedMarker, setSelectedMarker] = useAtom(selectedMarkerAtom)
  const { data: legendsData = {} } = useFetchLegends()
  const { layers } = useRiskLayersState()
  const selectedMapboxGeoJsonFeature = selectedFeature as MapboxGeoJSONFeature
  const visibleAssets = useAtomValue(visibleAssetsAtom)
  const regionMasks = useAtomValue(regionMasksAtom)
  const clientLocation = useAtomValue(configuredClientLocationAtom)
  const mapOverlayRef = useRef<HTMLDivElement>(null)
  const showSearchAddress = location.pathname.includes(appLink.risk.path)
  const previousMouseEvents = useRef<{ [key: string]: (e: MapLayerMouseEvent) => void }>({})
  const mouseEventsLayerIDTracker = useRef<string[]>([])
  const setMap = useSetAtom(riskMapInstanceAtom)
  const tooltipRef = useRef(new mapboxgl.Popup({ closeButton: false }))
  const assetLayers = getLayersByType(layers, 'asset')
  const hazardLayers = getLayersByType(layers, 'hazard')
  const informationLayers = getLayersByType(layers, 'information')

  const tooltipTimeoutRef = useRef<number | null>(null)
  const requestSeqRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const setMapClientLocation = useSetAtom(mapClientLocationAtom)

  useEffect(() => {
    if (clientLocation) {
      setMapClientLocation(clientLocation)
    }
  }, [clientLocation, setMapClientLocation])
  const cancelTooltipTimer = () => {
    if (tooltipTimeoutRef.current !== null) {
      clearTimeout(tooltipTimeoutRef.current)
      tooltipTimeoutRef.current = null
    }
  }
  const tooltipFeature = useRef<MapboxGeoJSONFeature | undefined>()

  async function controlMouseEvents(map: Map) {
    const rootSelectedLayerId = selectedMapboxGeoJsonFeature?.layer?.id
      .replace('-zoomed-out', '')
      .replace('-extrusion', '')

    if (
      selectedMarker &&
      selectedMapboxGeoJsonFeature &&
      rootSelectedLayerId &&
      layers.map((l) => l.id).includes(rootSelectedLayerId)
    ) {
      addSelectedMarkerToMap({
        map,
        coordinates: [selectedMarker.longitude, selectedMarker.latitude],
        colour: theme.palette.primary.main,
      })
    }

    const lookupTileSetIds = hazardLayers.reduce((acc, layer) => {
      if (layer.infoLookupTileset?.length) {
        for (const lookupTileSet of layer.infoLookupTileset) {
          if (lookupTileSet.id) acc.push(lookupTileSet.id)
        }
      }
      return acc
    }, [] as string[])

    if (Object.keys(visibleAssets).length >= 1) {
      addMouseEvents(map, [
        ...Object.keys(visibleAssets),
        ...informationLayers.map((layer) => layer.id),
        ...lookupTileSetIds,
      ])
    } else if (
      assetLayers.length >= 1 ||
      lookupTileSetIds.length >= 1 ||
      (informationLayers.length >= 1 && drawAreas.length === 0 && !regionMasks)
    ) {
      addMouseEvents(map, [
        ...assetLayers.map((layer) => layer.type),
        ...informationLayers.map((layer) => layer.id),
        ...lookupTileSetIds,
      ])
    }
  }

  function removeMouseEvents(map: Map) {
    const existingLayers = map
      .getStyle()
      .layers.filter((layer) => layer.id.includes('urbanintelligence'))
      .filter((layer) => !layer.id.includes('-line'))

    // both existingLayers and mouseEventsLayerIDTracker are necessary to remove mouse events
    existingLayers.forEach((layer) => {
      if (!layer) return
      map.off('mouseenter', layer.id, previousMouseEvents.current['mouseenter'])
      map.off('mousemove', layer.id, previousMouseEvents.current['mousemove'])
      map.off('mouseleave', layer.id, previousMouseEvents.current['mouseleave'])
      map.off('click', layer.id, previousMouseEvents.current['click'])
    })
    mouseEventsLayerIDTracker.current.forEach((layerId) => {
      map.off('mouseenter', layerId, previousMouseEvents.current['mouseenter'])
      map.off('mousemove', layerId, previousMouseEvents.current['mousemove'])
      map.off('mouseleave', layerId, previousMouseEvents.current['mouseleave'])
      map.off('click', layerId, previousMouseEvents.current['click'])
    })
    mouseEventsLayerIDTracker.current = []
  }

  async function addMouseEvents(map: Map, visibleAssetIds: string[]) {
    if (!interactive) return
    const popup = tooltipRef.current
    if (!popup) return

    const mapStyle = map.getStyle()
    if (!mapStyle) return

    const existingLayers = mapStyle.layers
      .filter((layer) => layer.id.includes('urbanintelligence'))
      .filter((layer) => !layer.id.includes('-line'))

    const isLayerInteractivityDisabled = (layer: { id: string; source?: string }) => {
      for (const assetLayer of assetLayers) {
        const tileset = assetLayer.tilesets.find((iLayer) => iLayer.id === layer.source)
        if (tileset) {
          return assetLayer.interactivityDisabled
        }
      }
      for (const infoLayer of informationLayers) {
        const tileset = infoLayer.tilesets.find((iLayer) => iLayer.id === layer.source)
        if (tileset) {
          return infoLayer.interactivityDisabled
        }
      }
      for (const hazardLayer of hazardLayers.filter((layer) => layer.infoLookupTileset?.length)) {
        const tileset = hazardLayer.tilesets.find((hLayer) => hLayer.id === layer.id)
        if (tileset) {
          return hazardLayer.interactivityDisabled
        }
      }

      return false
    }

    const layerIdMap = layers.map((layer) => {
      if (layer.infoLookupTileset?.length) return layer?.infoLookupTileset?.[0]?.id
      if (layer.tilesets) return layer.tilesets[0].id
      if (layer.id) return layer.id
      return ''
    })

    const interactiveLayers = visibleAssetIds
      .map((assetId) => {
        return existingLayers.filter((layer) => {
          // Some layers have a separate layer when zoomed out such as an icon layer where it uses points if zoomed out
          return (
            layer.id.includes(assetId) ||
            layer.id.includes(`${assetId}-zoomed-out`) ||
            layer.id.includes(`${assetId}-extrusion`)
          )
        })
      })
      .flat()
      .sort((a, b) => {
        return layerIdMap.findIndex((id) => id === a.id) - layerIdMap.findIndex((id) => id === b.id)
      })

    // Func to check if this feature's layer is higher priority than the one already shown
    // If true, that timeout will be replaced with a new one loading this layer
    // If no other layer is loading, this one will be loaded
    const isFeatureHigherPriorityThanAlreadyLoadingFeature = (feature: MapboxGeoJSONFeature) => {
      if (tooltipFeature.current == undefined) return true

      const layerId = feature.layer?.id.replace('-zoomed-out', '').replace('-extrusion', '') // Some layers are split into a zoomed in or zoomed out layer such as an icon layer.
      // use layers to sort instead of visibleAssetIds as visibleAssetIds is not in the correct order

      return (
        layerIdMap.findIndex((id) => id === layerId) <=
        layerIdMap.findIndex((id) => id === tooltipFeature.current?.layer?.id)
      )
    }

    const updateTooltip = (e: MapLayerMouseEvent) => {
      if (!e.features || !e.features.length) return
      // Only show tooltip if the feature has a value or asset_id
      // Which is the sign of an info feature that has a description to show or if it has an asset_id it likely has a tooltip sentence
      const hasSomeDescription = e.features.some((feature) => {
        return (
          (feature.properties && feature.properties.length > 0) ||
          feature.properties?.value ||
          feature.properties?.display_value ||
          feature.properties?.hover_comment ||
          feature.properties?.asset_id !== null ||
          feature.properties?.asset_id !== undefined
        )
      })

      if (!hasSomeDescription) return

      const interactivityIsDisabled = e.features.some((feature) => {
        const layer = interactiveLayers.find(
          (layer: mapboxgl.Layer) => layer?.source === feature.layer?.source,
        )
        const hazardLayerLookup = hazardLayers.find((hLayer) => {
          return (
            hLayer.infoLookupTileset?.length &&
            hLayer.infoLookupTileset.some((tileset) => tileset.id === feature.layer?.id)
          )
        })
        let flag: boolean | undefined = false
        if (hazardLayerLookup) {
          flag = isLayerInteractivityDisabled(hazardLayerLookup)
        } else if (layer) flag = isLayerInteractivityDisabled(layer)
        return flag
      })
      if (interactivityIsDisabled) return
      const hLayer = hazardLayers.find((hLayer) => {
        return (
          hLayer.infoLookupTileset?.length &&
          hLayer.infoLookupTileset.some((tileset) =>
            e.features?.some((feature: MapboxGeoJSONFeature) => feature.layer?.id === tileset.id),
          )
        )
      })
      let multiInfoLookupResults = null
      if (hLayer?.infoLookupTileset?.length ?? 0 > 1) {
        const res = map.queryRenderedFeatures(e.point)
        multiInfoLookupResults = res
      }

      map.getCanvas().style.cursor = 'pointer'

      popup.setLngLat(e.lngLat)
      popup.setMaxWidth('800px')

      // Return if the feature is already being hovered over.
      if (tooltipFeature.current && tooltipFeature.current.id === e.features[0].id) return

      // Return if the feature is lower priority than the one already loading
      if (!isFeatureHigherPriorityThanAlreadyLoadingFeature(e.features[0])) return

      // Set the feature as currently shown
      tooltipFeature.current = e.features[0]

      // Fade out outdated tooltip content (this typecast is safe btw)
      const content = document.getElementsByClassName('tooltip-container')[0] as HTMLElement
      if (content) content.style.color = '#AAA'
      if (requestSeqRef.current !== null) {
        clearTimeout(requestSeqRef.current)
      }

      requestSeqRef.current = setTimeout(async () => {
        if (!tooltipFeature.current) return popup.remove()
        const details = await getAssetAndHazardDetailsForSelectedFeature(
          tooltipFeature.current,
          assetLayers,
          hazardLayers,
          legendsData,
        )
        const information = getInformationDetailsForSelectedFeature(
          tooltipFeature.current,
          informationLayers,
        )
        const hazardInformation = getHazardInfoForSelectedFeature(
          tooltipFeature.current,
          hazardLayers,
        )

        // for Mapbox NIWA data storage compression by splitting scenarios into separate layers
        if (hazardInformation && !hazardInformation.value && multiInfoLookupResults) {
          if (hLayer && hLayer.assetTag) {
            const infoLookupRes = multiInfoLookupResults.find((feature) =>
              hazardLayers.some(
                (layer) =>
                  feature.properties &&
                  Object.keys(feature.properties).some((property) => property === layer.assetTag),
              ),
            )
            if (!infoLookupRes || !infoLookupRes.properties) return
            hazardInformation.value = infoLookupRes.properties[hLayer.assetTag]
          }
        }
        if (!details && !information && !hazardInformation) return popup.remove()

        const asset = details?.asset
        const hazards = details?.hazards

        const scenariosByAsset: ScenariosByAsset[] = asset
          ? (await apiClient.get(`/hazard/scenarios/${asset.type}/${asset.id}`)).data
          : []
        const knownRiskScenarioIds = scenariosByAsset
          .filter((s) => s.is_exposed == 'true')
          .map((scenario) => scenario.hazard_scenario)

        const knownRiskIcons: Set<string> = new Set<string>()
        knownRiskScenarioIds.forEach((knownRiskScenarioId) => {
          const knownRisk = layerData?.hazardDialogData.hazardDetails.find((hazardDetail) =>
            hazardDetail.scenarios.find((s) => s.assetTag == knownRiskScenarioId),
          )

          // Hacky way to check if element is exposed to a mapped hazard as knownRisk diesn't have any useful ids
          // and seeing as this is only for CCC I'd rather not change the API to do a better job.
          // If the scenario id format changes the only risk is an extra icon in the list.
          const riskIsOnMap =
            Array.isArray(hazards) &&
            hazards?.some((hazard) => knownRiskScenarioId.startsWith(`h${hazard.hazard_id}`))
          if (knownRisk && !riskIsOnMap) knownRiskIcons.add(knownRisk.icon)
        })

        const enrichedHazards =
          isDamageCostEnabledRef.current &&
          asset?.type &&
          asset?.id &&
          Array.isArray(hazards) &&
          hazards.length
            ? await enrichHazardsWithDamageCost(asset.type, asset.id, hazards)
            : hazards

        const mapToolTip = RiskMapTooltip({
          asset,
          hazards: enrichedHazards,
          information: information || hazardInformation,
          mainColour: theme.palette.primary.main,
          prefer_hazard_term: term_preference.hazard,
          prefer_risk_term: term_preference.risk,
          knownRiskIcons: Array.from(knownRiskIcons),
        })

        if (!mapToolTip) return
        popup.setHTML(ReactDOMServer.renderToString(mapToolTip))
        popup.addTo(map)
      }, 400)
    }

    // MOUSE EVENTS
    const mouseEnterEvent = function (e: MapLayerMouseEvent) {
      if (!e) return
      if (interactive) updateTooltip(e)
    }

    const mouseMoveEvent = (e: MapLayerMouseEvent) => {
      if (interactive) updateTooltip(e)
    }

    const mouseLeaveEvent = (_e: MapLayerMouseEvent) => {
      cancelTooltipTimer()
      tooltipFeature.current = undefined
      map.getCanvas().style.cursor = ''
      popup.remove()
    }

    let selectFeatureTimeout: null | NodeJS.Timeout = null
    const mouseClickEvent = async (e: MapLayerMouseEvent) => {
      let coordinates = [e.lngLat.lng, e.lngLat.lat]

      if (typeof e.features === 'undefined') return
      const feature = e.features[0]

      if (!feature) return

      const featureCenter = getCenterOfFeature(feature)
      if (featureCenter) {
        coordinates = featureCenter
      }

      const selectedAsset = assetLayers.find(
        (assetLayer) => assetLayer.tilesets[0].id === feature.layer?.source,
      )
      const selectedContextLayer = informationLayers.find(
        (infoLayer) => infoLayer.tilesets[0].id === feature.layer?.source,
      )

      if (!selectFeatureTimeout) {
        // If feature already selected and not on selection tab
        // it wouldn't return to selection tab without deselecting it first.
        deselectFeature()
        selectFeatureTimeout = setTimeout(() => {
          selectFeatureTimeout = null
          let successfulSelect = false
          if (selectedContextLayer) {
            successfulSelect = selectFeature(feature, selectedContextLayer, 'selection')
          }
          if (selectedAsset) {
            successfulSelect = selectFeature(feature, selectedAsset, 'selection')
          }
          if (successfulSelect) {
            addSelectedMarkerToMap({
              map,
              coordinates: coordinates as mapboxgl.LngLatLike,
              colour: theme.palette.primary.main,
            })

            setSelectedMarker({
              longitude: coordinates[0],
              latitude: coordinates[1],
            })
          }
          if (requestSeqRef.current !== null) {
            clearTimeout(requestSeqRef.current)
          }
          tooltipFeature.current = undefined
          tooltipRef.current.remove()
        }, 100)
      }
    }

    const mouseEvents: { [key: string]: (e: MapLayerMouseEvent) => void } = {
      mouseenter: mouseEnterEvent,
      mousemove: mouseMoveEvent,
      mouseleave: mouseLeaveEvent,
      click: mouseClickEvent,
    }

    Object.keys(mouseEvents).forEach((eventName) => {
      if (previousMouseEvents.current[eventName] == undefined) {
        previousMouseEvents.current[eventName] = mouseEvents[eventName]
      }
    })

    // Clear existing events and add new ones
    mouseEventsLayerIDTracker.current.forEach((layerId) => {
      map.off('mouseenter', layerId, previousMouseEvents.current['mouseenter'])
      map.off('mousemove', layerId, previousMouseEvents.current['mousemove'])
      map.off('mouseleave', layerId, previousMouseEvents.current['mouseleave'])
      map.off('click', layerId, previousMouseEvents.current['click'])
    })
    mouseEventsLayerIDTracker.current = []

    Object.keys(mouseEvents).forEach((eventName) => {
      previousMouseEvents.current[eventName] = mouseEvents[eventName]
    })

    interactiveLayers.forEach((layer) => {
      if (!layer) return
      map.on('click', layer.id, previousMouseEvents.current['click'])
      map.on('mouseenter', layer.id, previousMouseEvents.current['mouseenter'])
      map.on('mousemove', layer.id, previousMouseEvents.current['mousemove'])
      map.on('mouseleave', layer.id, previousMouseEvents.current['mouseleave'])
      mouseEventsLayerIDTracker.current.push(layer.id)
    })
  }

  useEffect(() => {
    if (clientLocation) {
      setMapClientLocation(clientLocation)
    }
  }, [clientLocation, setMapClientLocation])

  // The map instance is recreated on basemap change; only fly when the client location
  // itself changes, otherwise switching basemap snaps the camera back to the start.
  const flownClientLocationRef = useRef<typeof clientLocation | null>(null)
  useEffect(() => {
    if (!clientLocation || !map || !flyToClientLocation) return
    if (flownClientLocationRef.current === clientLocation) return
    flownClientLocationRef.current = clientLocation

    flyTo({
      ...clientLocation,
      usePadding: true,
      pitch: 0,
      bearing: 0,
    })
  }, [clientLocation, map, flyToClientLocation, flyTo])

  useEffect(() => {
    async function addMapLayersAndEvents() {
      if (map) await controlMouseEvents(map)
    }

    addMapLayersAndEvents()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map])

  useEffect(() => {
    if (!map || !selectedMapboxGeoJsonFeature || !layers) return

    const rootSelectedLayerId = selectedMapboxGeoJsonFeature?.layer?.id
      .replace('-zoomed-out', '')
      .replace('-extrusion', '')

    if (!rootSelectedLayerId || !layers.map((l) => l.id).includes(rootSelectedLayerId)) {
      deselectFeature()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layers])

  useEffect(() => {
    if (!map) return
    tooltipRef.current.remove()
    controlMouseEvents(map)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layers, visibleAssets, selectedMarker])

  useEffect(() => {
    return () => cancelTooltipTimer()
  }, [])

  useEffect(() => {
    if (!map) return
    if (interactive) {
      const lookupTileSetIds = hazardLayers.reduce((acc, layer) => {
        if (layer.infoLookupTileset?.length) {
          for (const lookupTileSet of layer.infoLookupTileset) {
            if (lookupTileSet.id) acc.push(lookupTileSet.id)
          }
        }
        return acc
      }, [] as string[])
      if (Object.keys(visibleAssets).length >= 1) {
        addMouseEvents(map, [
          ...Object.keys(visibleAssets),
          ...informationLayers.map((layer) => layer.id),
          ...lookupTileSetIds,
        ])
      } else if (
        assetLayers.length >= 1 ||
        lookupTileSetIds.length >= 1 ||
        (informationLayers.length >= 1 && drawAreas.length === 0 && !regionMasks)
      ) {
        addMouseEvents(map, [
          ...assetLayers.map((layer) => layer.type),
          ...informationLayers.map((layer) => layer.id),
          ...lookupTileSetIds,
        ])
      }
    } else removeMouseEvents(map)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interactive])

  const legendIds = useMemo(() => {
    const visibleLayers = layers.filter((layer) => layer.visible)
    const visibleLayerLegends = visibleLayers.map((layer) => layer.legend)
    return visibleLayerLegends.filter((legend) => legend !== '')
  }, [layers])

  const showVulnerabilityLegend = useMemo(() => {
    return (
      layers.some((l) => l.layerType === 'hazard') && layers.some((l) => l.layerType === 'asset')
    )
  }, [layers])

  const noVulnerabilityAssets = useMemo(() => {
    const noVulnerabilityAssets: string[] = []
    if (!layers.some((l) => l.layerType === 'hazard')) {
      const elements = layers.flatMap((l) => {
        if (l.layerType === 'asset') {
          return l.display_name
        }
        return []
      })
      noVulnerabilityAssets.push(...elements)
    }
    return noVulnerabilityAssets
  }, [layers])

  return (
    <>
      <GenericMapView
        setMapInstance={setMap}
        flyToClientLocation={flyToClientLocation}
        map={map}
        customStyle={css`
          & .mapboxgl-ctrl-top-right .mapboxgl-ctrl {
            margin-top: ${showSearchAddress ? '116px' : '80px'};
          }
        `}
        mapPaddingRef={mapPaddingRef}
        inContainerSlot={
          drawAreas.length ? (
            <div id="selection-measurement" css={selectionMeasurementContainer}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <h6 css={selectionMeasurementHeader}>Selection Details:</h6>
                <span>
                  Area: <span id="selection-calculated-area"></span>
                </span>
                <span>
                  Length: <span id="selection-calculated-length"></span>
                </span>
              </div>
            </div>
          ) : null
        }
        outsideContainerSlot={
          showAddLayersModalTab || openAddLayersSelectionDialog
            ? createPortal(
                <Modal
                  open={!!showAddLayersModalTab || openAddLayersSelectionDialog}
                  onClose={() => {
                    setShowAddLayersModalTab('')
                    setOpenAddLayersSelectionDialog(false)
                  }}
                >
                  <AddLayersDialog
                    onClose={() => {
                      setShowAddLayersModalTab('')
                      setOpenAddLayersSelectionDialog(false)
                    }}
                  />
                </Modal>,
                document.body,
              )
            : null
        }
      />
      <Box ref={mapOverlayRef} css={mapOverlayContainer}>
        <Box css={mapTopRightContainer}>
          <Box
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}
          >
            <RiskMapTools mapOverlayRef={mapOverlayRef} />
            {showSearchAddress && <GeocoderButton map={map} />}
            <ThreeDButton />
          </Box>
        </Box>
        <Box css={mapBottomLeftContainer}>
          <BaseMapButton />
        </Box>
        <Box css={mapBottomRightContainer({ theme })}>
          <MapLegends
            legendIds={legendIds}
            uiBlueLegendTitles={noVulnerabilityAssets}
            showVulnerabilityLegend={showVulnerabilityLegend}
          />
        </Box>
      </Box>
    </>
  )
}
